/**
 * REST auth endpoints — DOC1 §11.1.
 *
 *   POST /auth/otp/request   — request an OTP for a mobile number
 *   POST /auth/otp/verify    — verify OTP; returns 24h JWT + refresh
 *   POST /auth/refresh       — exchange refresh for a fresh JWT
 *   POST /auth/logout        — invalidate refresh token (T019 ratified)
 *   DELETE /auth/me          — delete the caller's own account (204)
 */
import { z } from 'zod';
import { E164MobileSchema } from '../common/mobile.js';
import { Iso8601Schema } from '../common/timestamp.js';
import { OtpRules } from '../auth/rules.js';
import { AppRoleSchema } from '../auth/roles.js';
// ── shared ─────────────────────────────────────────────────────────────────
const OtpCodeSchema = z
    .string()
    .length(OtpRules.length)
    .regex(/^\d+$/, 'OTP must be all digits');
export const AuthErrorCodeSchema = z.enum([
    'OTP_INVALID',
    'OTP_EXPIRED',
    'OTP_BLOCKED',
    'OTP_THROTTLED',
    'REFRESH_INVALID',
    'REFRESH_EXPIRED',
    'USER_BLOCKED',
    'USER_NOT_REGISTERED',
]);
// ── POST /auth/otp/request ─────────────────────────────────────────────────
export const OtpRequestBody = z.object({
    mobileNumber: E164MobileSchema,
    /** 'login' checks the number exists first; 'register' allows new numbers. */
    mode: z.enum(['login', 'register']).default('login'),
});
export const OtpRequestResponse = z.object({
    /** When the next /auth/otp/request will be allowed for this mobile (DOC1 §7.3 — 30s gate). */
    resendAvailableAt: Iso8601Schema,
});
// ── POST /auth/otp/verify ──────────────────────────────────────────────────
export const OtpVerifyBody = z.object({
    mobileNumber: E164MobileSchema,
    otp: OtpCodeSchema,
    /** Only applied when the underlying users row is first created (i.e. on
     *  registration) — ignored on every subsequent login so it can never
     *  overwrite a name the user later sets via PATCH /auth/me. */
    name: z.string().min(1).max(120).optional(),
});
export const AuthenticatedUserSchema = z.object({
    /** Displayed in web app sidebars. */
    mobile: E164MobileSchema,
    /** Null if never set — used for dashboard greeting. */
    name: z.string().nullable(),
    /** Routes production_test users into Test Mode on mobile. */
    role: AppRoleSchema.optional(),
});
export const OtpVerifyResponse = z.object({
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1),
    user: AuthenticatedUserSchema,
});
// ── POST /auth/refresh ─────────────────────────────────────────────────────
export const RefreshBody = z.object({
    refreshToken: z.string().min(1),
});
export const RefreshResponse = z.object({
    accessToken: z.string().min(1),
});
// ── POST /auth/logout (T019 — ratified from DOC4 MA-26) ────────────────────
export const LogoutBody = z.object({
    refreshToken: z.string().min(1),
});
/** 204 No Content — empty body. */
export const LogoutResponse = z.object({}).strict();
// ── DELETE /auth/me — delete own account ────────────────────────────────────
//
// Permanently removes the caller's user row and everything keyed to it
// (member links, shares, push tokens, notifications, feedback…). Devices the
// caller owns are unlinked (owner_id → NULL) — the physical devices and their
// registry rows are never deleted. Users holding an admin-panel role are
// refused (`CANNOT_DELETE_ADMIN`). The optional refreshToken lets the server
// revoke the caller's current refresh family along with the access token.
export const DeleteAccountBody = z.object({
    refreshToken: z.string().min(1).optional(),
});
/** 204 No Content — empty body. */
export const DeleteAccountResponse = z.object({}).strict();
// ── /auth/me — profile read + update + avatar ──────────────────────────────
//
//   GET    /auth/me            → ProfileResponse
//   PATCH  /auth/me            → ProfileResponse  (name + language)
//   PUT    /auth/me/avatar     → ProfileResponse  (replace photo)
//   DELETE /auth/me/avatar     → ProfileResponse  (clear photo)
//   GET    /auth/me/avatar     → image bytes      (authenticated)
//
// Avatar is stored inline on `users` (bytea) — matches the seller invoice flow.
// `profileImageUrl` is the relative API path the client should fetch to get
// the bytes, or `null` if no image is set.
export const ProfileResponse = AuthenticatedUserSchema.extend({
    /** Relative API path to fetch the avatar bytes (auth required), or null. */
    profileImageUrl: z.string().nullable(),
    /** Last upload time, ISO-8601, or null. Useful for cache-busting. */
    profileImageUpdatedAt: Iso8601Schema.nullable(),
});
export const ProfileUpdateBody = z.object({
    name: z.string().min(1).max(120).optional(),
    language: z.string().min(2).max(8).optional(),
});
// ── Change login mobile number (OTP to the NEW number) ──────────────────────
export const ChangeMobileRequestBody = z.object({ newMobile: E164MobileSchema });
export const ChangeMobileRequestResponse = z.object({});
export const ChangeMobileVerifyBody = z.object({
    newMobile: E164MobileSchema,
    otp: OtpCodeSchema,
});
// verify returns ProfileResponse (the updated profile).
export const AvatarUploadBody = z.object({
    base64: z.string().min(1),
    filename: z.string().min(1).max(255).optional(),
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
});
//# sourceMappingURL=auth.js.map